// Content review types — mirrors the AI service's /video/review/{analysis_id}
// response shape. Defensive: every field on `ContentReviewResult` is optional
// because LLM outputs occasionally omit keys; the UI hides empty sections
// rather than crash.

// GeoAiEnvelope is the BE/AI contract source of truth, declared once in
// @discovr/types (packages/types/src/script-review.ts) and re-exported here
// so `from './types'` imports in this directory (matching every other type
// below) don't need a separate `@discovr/types` import. Same envelope shape
// Script Review's `geoAnalysis` prop uses — see ScriptReview/types.ts, which
// re-exports the same type for the same reason.
export type { GeoAiEnvelope } from '@discovr/types';

export type ContentReviewAiEnvelope = {
  status: 'pending' | 'complete' | 'failed';
  analysis_id?: string;
  data?: ContentReviewBody;
  error?: string;
};

// Full AI body returned by ai-service GET /video/review/{id}. `result`
// carries the LLM-normalised review. Run-info fields (tier_name, model_used,
// duration_s, est_input_tokens, encoder, safety_filter_retried) are gated
// behind `?debug=run_info` and surface only under the optional `_debug` key;
// they are intentionally absent from the default payload.
export type ContentReviewBody = {
  result?: ContentReviewResult;
  analysis_id?: string;
  roster_entry_id?: string;
  computed_at?: string;          // ISO 8601 UTC
};

// AI's normalised case-sensitive enums. Keep the casing AI emits ("Low",
// "Pass") so consumers don't have to lowercase before comparing.
export type ContentSeverity = 'Low' | 'Medium' | 'High' | string;
export type ContentRiskLevel = 'Low' | 'Moderate' | 'High' | string;
export type ContentStatusRecommendation =
  | 'Approved'
  | 'Needs Revision'
  | 'Rejected'
  | string;
export type PassFailStatus = 'Pass' | 'Fail' | string;

export type TimestampedIssue = {
  timestamp: string;             // "MM:SS"
  issue: string;
  severity: ContentSeverity;
  fix: string;
};

export type PassFailEntry = {
  notes: string;
  status: PassFailStatus;
};

export type AutomatedCheck = {
  title: string;
  passed: boolean;
  details: string;
  reasoning: string;
  recommendation: string;
};

export type ExactFix = {
  original: string;
  replacement: string;
};

export type AdvancedAnalysis = {
  hook_effectiveness?: string;
  cta_effectiveness?: string;
  pacing_notes?: string;
  audio_quality?: string;
  visual_quality?: string;
  thumbnail_recommendation?: string;
};

export type HumanInLoop = {
  requires_manual_review?: boolean;
  manual_review_reasons?: string[];
};

/**
 * DetectedProduct — the product the AI determined the video actually promotes,
 * from content alone (Multi-Product Campaigns). Mirrors the Python
 * VIDEO_REVIEW_SCHEMA `detected_product`. The FE compares
 * `primary_product_promoted` against the creator's assigned products to render a
 * ✓/✗ product-match verdict. All fields optional — defensive against partial AI
 * output; the card only renders when `primary_product_promoted` is present.
 */
export type DetectedProduct = {
  primary_product_promoted?: string;
  brand?: string;
  confidence?: string;
  evidence_timestamp?: string;
};

export type ContentReviewResult = {
  script_extraction?: string;
  status_recommendation?: ContentStatusRecommendation;
  publish_score?: number;        // 0–100
  risk_level?: ContentRiskLevel;
  feedback?: string;
  strengths?: string[];
  human_in_loop?: HumanInLoop;
  pass_fail_checklist?: Record<string, PassFailEntry>;
  timestamped_issues?: TimestampedIssue[];
  risk_flags?: string[];
  exact_fixes?: ExactFix[];
  automated_checks?: AutomatedCheck[];
  advanced_analysis?: AdvancedAnalysis;
  /** AI-detected promoted product (Multi-Product Campaigns). */
  detected_product?: DetectedProduct;
};

// ── Tone helpers ─────────────────────────────────────────────────────────────

export function severityTone(severity: ContentSeverity): {
  bg: string;
  text: string;
  border: string;
  dot: string;
} {
  switch (severity) {
    case 'Low':
      return { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-300', dot: 'bg-emerald-500' };
    case 'Medium':
      return { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300', dot: 'bg-amber-500' };
    case 'High':
      return { bg: 'bg-rose-100', text: 'text-rose-800', border: 'border-rose-400', dot: 'bg-rose-500' };
    default:
      return { bg: 'bg-muted', text: 'text-muted-foreground', border: 'border-border', dot: 'bg-stone-400' };
  }
}

export function riskLevelTone(risk: ContentRiskLevel): {
  bg: string;
  text: string;
  border: string;
} {
  switch (risk) {
    case 'Low':
      return { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' };
    case 'Moderate':
      return { bg: 'bg-amber-50', text: 'text-amber-800', border: 'border-amber-200' };
    case 'High':
      return { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200' };
    default:
      return { bg: 'bg-muted', text: 'text-muted-foreground', border: 'border-border' };
  }
}

export function passFailTone(status: PassFailStatus): {
  bg: string;
  text: string;
  border: string;
} {
  if (status === 'Pass') return { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' };
  if (status === 'Fail') return { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200' };
  return { bg: 'bg-muted', text: 'text-muted-foreground', border: 'border-border' };
}

export function statusRecTone(rec: ContentStatusRecommendation): {
  bg: string;
  text: string;
  border: string;
} {
  switch (rec) {
    case 'Approved':
      return { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' };
    case 'Needs Revision':
      return { bg: 'bg-amber-50', text: 'text-amber-800', border: 'border-amber-200' };
    case 'Rejected':
      return { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200' };
    default:
      return { bg: 'bg-muted', text: 'text-muted-foreground', border: 'border-border' };
  }
}

// Severity sort: High → Medium → Low → other (stable for everything else).
export function severityRank(severity: ContentSeverity): number {
  if (severity === 'High') return 0;
  if (severity === 'Medium') return 1;
  if (severity === 'Low') return 2;
  return 3;
}

// Pretty-print snake_case keys ("brand_tone" → "Brand Tone").
export function humanizeKey(key: string): string {
  return key
    .split('_')
    .map((part) => (part && part.length > 0 ? part.charAt(0).toUpperCase() + part.slice(1) : ''))
    .join(' ');
}
